import { CheckOutIdentity } from "../types";
import { buildCheckOutStamp, parseCheckOutStamp, CHECK_OUT_STAMP_ROOT } from "./checkOutStamp";

// Reading the <jyloCheckOut> stamp out of a custom XML part collection.
//
// Excel and PowerPoint expose byte-for-byte the same shape for this -- getXml(),
// setXml(), delete(), add(), items -- differing only in which run function opens the
// context and whether the collection hangs off context.workbook or
// context.presentation. So this is written once and parameterised, rather than being
// the same forty lines twice.
//
// Word is deliberately NOT routed through here: it predates this API shape and reads
// an `xml` property after context.load(part, "xml") instead of calling getXml(),
// which is a different enough loop that sharing it would mean a flag.

/** What both Excel.CustomXmlPart and PowerPoint.CustomXmlPart satisfy. */
interface XmlPart {
  getXml(): { value: string };
  delete(): void;
}

interface XmlPartCollection<P extends XmlPart> {
  readonly items: P[];
  add(xml: string): unknown;
  load(propertyNames: string): unknown;
  /** Only for the diagnostic below -- the read itself goes through `items`. */
  getCount(): { value: number };
}

interface SyncContext {
  sync(): Promise<void>;
}

export interface CustomXmlPartHost<C extends SyncContext, P extends XmlPart> {
  /** Excel.run / PowerPoint.run. */
  run<T>(callback: (context: C) => Promise<T>): Promise<T>;
  /** Where the collection hangs off this host's context. */
  parts(context: C): XmlPartCollection<P>;
  /** For the log line when a read fails. */
  hostName: string;
}

export interface CustomXmlIdentityStore {
  read(): Promise<CheckOutIdentity | null>;
  write(identity: CheckOutIdentity): Promise<void>;
}

export function createCustomXmlIdentityStore<C extends SyncContext, P extends XmlPart>(
  host: CustomXmlPartHost<C, P>
): CustomXmlIdentityStore {
  return {
    async read(): Promise<CheckOutIdentity | null> {
      try {
        return await host.run(async (context) => {
          const parts = host.parts(context);
          // Queued alongside the load purely so a fruitless read can tell the two
          // failures apart: a count of 0 means the host genuinely has no parts, while a
          // count above items.length means it has them and this load did not
          // materialise them -- which would be our bug, not the host's.
          const count = parts.getCount();
          parts.load("items");
          await context.sync();

          // Queue every getXml() before syncing: one round trip instead of one per
          // part, which matters on a document carrying several parts.
          const pending = parts.items.map((part) => part.getXml());
          await context.sync();

          for (const result of pending) {
            const identity = parseCheckOutStamp(result.value);
            if (identity) return identity;
          }

          // No stamp found. This is the correct answer for a document that was never
          // checked out -- and a symptom when it WAS -- and the two are impossible to
          // tell apart from the pane, which renders no buttons either way. So say what
          // was actually there. An empty collection means the host never saw the stamp
          // at all (it was not written, or not related from the part this API
          // enumerates); parts present with no <jyloCheckOut> among them means the host
          // saw them and none was ours, which is a different bug entirely.
          // getCount() hands back a ClientResult, which the syncs above fill and which
          // is never load()ed -- the rule cannot see that through the structural
          // interface this file is written against, so it asks for a load that has no
          // meaning here.
          // eslint-disable-next-line office-addins/load-object-before-read -- ClientResult, not a loadable proxy
          const reported = count.value;
          console.log(
            `No <${CHECK_OUT_STAMP_ROOT}> part in ${host.hostName}. ` +
              `getCount()=${reported}, items=${pending.length}: ` +
              JSON.stringify(pending.map((result) => result.value.slice(0, 200)))
          );

          return null;
        });
      } catch (error) {
        // Deliberately rethrown, not swallowed into null.
        //
        // An absent stamp and a failed read are DIFFERENT states, and an earlier
        // version of this treated them alike. That turned any host error into "this
        // document is not checked out from Jylo" -- said confidently, about a document
        // that plainly was, which sent people looking for a check-out bug instead of an
        // API one. Null means "no stamp here"; a rejection means "could not look".
        console.log(`Error reading check out identity in ${host.hostName}: ` + error);
        throw error;
      }
    },

    async write(identity: CheckOutIdentity): Promise<void> {
      await host.run(async (context) => {
        host.parts(context).add(buildCheckOutStamp(identity));
        await context.sync();
      });
    },
  };
}
